// lib/calendar.ts
// Shared calendar utilities using URL query params for state management
import { useMemo } from "react";
import { router, useLocalSearchParams } from "expo-router";
import { MMKV } from "react-native-mmkv";

const storage = new MMKV();
export const CALENDAR_DAYS_VIEW_KEY = "calendar-daysView";
export const DEFAULT_DAYS_VIEW = 7;

// Parse date from query param string (YYYY-MM-DD format)
export function parseDateParam(dateStr: string | string[] | undefined): Date {
  if (!dateStr || Array.isArray(dateStr)) return new Date();
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

// Format date for URL param (YYYY-MM-DD)
export function formatDateParam(date: Date): string {
  return date.toISOString().split("T")[0];
}

// Parse daysView from query param
export function parseDaysViewParam(daysStr: string | string[] | undefined): number {
  if (!daysStr || Array.isArray(daysStr)) return DEFAULT_DAYS_VIEW;
  const parsed = parseInt(daysStr, 10);
  return isNaN(parsed) ? DEFAULT_DAYS_VIEW : Math.max(1, Math.min(14, parsed));
}

// Navigation: step by days
export function stepDay(direction: "left" | "right", currentDate: Date) {
  const newDate = new Date(currentDate);
  newDate.setDate(currentDate.getDate() + (direction === "right" ? 1 : -1));
  router.setParams({ date: formatDateParam(newDate) });
}

// Navigation: step by week
export function stepWeek(direction: "left" | "right", currentDate: Date) {
  const newDate = new Date(currentDate);
  newDate.setDate(currentDate.getDate() + (direction === "right" ? 7 : -7));
  router.setParams({ date: formatDateParam(newDate) });
}

// Set days view and persist to storage
export function setDaysView(days: number) {
  storage.set(CALENDAR_DAYS_VIEW_KEY, days);
  router.setParams({ daysView: days.toString() });
}

// Navigation: go to today
export function goToToday() {
  router.setParams({ date: formatDateParam(new Date()) });
}

// Navigation: set specific date
export function setDate(newDate: Date) {
  router.setParams({ date: formatDateParam(newDate) });
}

// Hook to get calendar params
export function useCalendarParams() {
  const params = useLocalSearchParams<{ date?: string; daysView?: string }>();

  // Memoize to prevent new Date object on every render when params.date is undefined
  const date = useMemo(() => parseDateParam(params.date), [params.date]);
  const daysView = parseDaysViewParam(params.daysView);

  return {
    date,
    daysView,
    stepDay: (direction: "left" | "right") => stepDay(direction, date),
    stepWeek: (direction: "left" | "right") => stepWeek(direction, date),
    setDaysView,
    goToToday,
    setDate,
  };
}
